/**
 * DataPrivacy Page
 *
 * Settings page for data export, retention info, consent management,
 * and compliance transparency.
 * Based on SETTINGS_FUNCTIONAL_SPEC.md Section 4 "Data & Privacy Page"
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SettingsLayout } from '../../components/settings/SettingsLayout';
import { ConsentManager } from '../../components/ConsentManager';
import {
  Download,
  Check,
  Trash2,
  Loader2,
  FileText,
  Shield,
} from 'lucide-react';

export function DataPrivacy(): JSX.Element {
  // UI state
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExportData = async () => {
    setIsExporting(true);

    try {
      // Placeholder for data export functionality
      // In a real app, this would call a Cloud Function to generate an export
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 5000);
    } catch (err) {
      console.error('Error exporting data:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <SettingsLayout>
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-[28px] font-bold text-ink">Data & Privacy</h2>
        <p className="text-muted text-[15px]">Manage your data and privacy settings</p>
      </div>

      {/* Export Your Data Card */}
      <div className="card mb-6">
        <div className="p-6">
          <h3 className="text-lg font-bold text-ink mb-1">Export Your Data</h3>
          <p className="text-sm text-muted mb-5">
            Download all your data including documents, logs, and letters
          </p>

          {/* Export Contents List */}
          <div className="bg-airy rounded-lg p-4 mb-5">
            <p className="text-sm text-muted mb-3">Your export will include:</p>
            <ul className="space-y-2 text-sm text-ink">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-ink rounded-full" />
                All uploaded documents (original files)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-ink rounded-full" />
                Care log entries (JSON + PDF report)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-ink rounded-full" />
                Generated letters and templates
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-ink rounded-full" />
                Provider and insurance information
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-ink rounded-full" />
                Account settings and preferences
              </li>
            </ul>
          </div>

          {exportSuccess ? (
            <div className="flex items-center gap-2 text-evergreen">
              <Check className="w-5 h-5" />
              <span className="text-sm font-medium">
                Your data export has been initiated. You will receive an email with the download link.
              </span>
            </div>
          ) : (
            <button
              onClick={handleExportData}
              disabled={isExporting}
              className="btn-primary flex items-center gap-2"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparing Export...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download All Data
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Consent Management Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-5">
            <FileText className="w-5 h-5 text-evergreen" />
            <h3 className="text-lg font-bold text-ink">Consent Management</h3>
          </div>

          <ConsentManager />
        </div>
      </div>

      {/* Data Retention Card */}
      <div className="card mb-6">
        <div className="p-6">
          <h3 className="text-lg font-bold text-ink mb-5">Data Retention</h3>

          {/* Active Account Data */}
          <div className="flex gap-3 p-4 bg-airy rounded-lg mb-3">
            <div className="w-6 h-6 bg-evergreen rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="w-3 h-3 text-white" />
            </div>
            <div>
              <p className="font-semibold text-ink mb-1">Active Account Data</p>
              <p className="text-sm text-muted">
                Your data is retained as long as your account is active. We never sell or share
                your personal health information.
              </p>
            </div>
          </div>

          {/* After Account Deletion */}
          <div className="flex gap-3 p-4 bg-airy rounded-lg">
            <div className="w-6 h-6 bg-soft-neutral rounded-full flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-3 h-3 text-muted" />
            </div>
            <div>
              <p className="font-semibold text-ink mb-1">After Account Deletion</p>
              <p className="text-sm text-muted">
                All personal data is permanently deleted within 30 days of account deletion.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Commitment Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-5">
            <Shield className="w-5 h-5 text-evergreen" />
            <h3 className="text-lg font-bold text-ink">Our Commitment to You</h3>
          </div>

          <p className="text-sm text-ink leading-relaxed mb-4">
            We understand you're entrusting Hesvara with sensitive information about your
            loved one's care. That trust is not something we take lightly.
          </p>
          <p className="text-sm text-ink leading-relaxed mb-4">
            Hesvara is built with healthcare-grade security standards. We implement technical
            and administrative safeguards comparable to those required of healthcare providers,
            because your health information deserves that level of protection.
          </p>
          <p className="text-sm text-ink leading-relaxed">
            For more detail, see{' '}
            <Link to="/privacy" className="text-evergreen hover:underline font-medium">
              Hesvara's Data Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </SettingsLayout>
  );
}

export default DataPrivacy;
